import { COMMON_CONSTANTS, GENDER } from '@constants/index';
import { CHILD_HEADER_LABELS, CHILD_OVERVIEW_LABELS } from '../pages/Beneficiary-block/WomenComponent/Child/ChildtrackingConstant';
import { decodeWithKeyAndShift } from "@utils/base64Utils";
import { toRoman } from "@utils/commonUtils";
import { toast } from "react-toastify";
import React from 'react';
import { ABDM_ERROR_CODES, CHILD_ERROR_MESSAGES, child_otp_const } from '@pages/Beneficiary-block/WomenComponent/Child/childConstants';
import { benef_error_const } from '@constants/beneficiaryConstant';

const DEFAULT_VALUES = {
    FATHER_NAME: { label: CHILD_OVERVIEW_LABELS.DEFAULT_FATHER_NAME, value: '-' },
    FATHER_MOBILE: { label: CHILD_OVERVIEW_LABELS.DEFAULT_FATHER_MOBILE, value: '-' },
    MOTHER_NAME: { label: CHILD_OVERVIEW_LABELS.DEFAULT_MOTHER_NAME, value: '-' },
    MOTHER_MOBILE: { label: CHILD_OVERVIEW_LABELS.DEFAULT_MOTHER_MOBILE, value: '-' },
    GUARDIAN_NAME: { label: CHILD_OVERVIEW_LABELS.DEFAULT_GUARDIAN_NAME, value: '-' },
    GUARDIAN_MOBILE: { label: CHILD_OVERVIEW_LABELS.DEFAULT_GUARDIAN_MOBILE, value: '-' },
  };

export const getDefaultDetails = (parentGender: string, maturityStatus: string, isMotherAlive: boolean) => {
    const defaultDetails = [];
    if (parentGender === GENDER.FEMALE) {
      if (maturityStatus === COMMON_CONSTANTS.MOTHER) {
        defaultDetails.push(
            DEFAULT_VALUES.FATHER_NAME,
            DEFAULT_VALUES.FATHER_MOBILE,
            DEFAULT_VALUES.GUARDIAN_NAME,
            DEFAULT_VALUES.GUARDIAN_MOBILE
        );
      } else if (maturityStatus === COMMON_CONSTANTS.GUARDIAN) {
        if (isMotherAlive) {
          defaultDetails.push(
            DEFAULT_VALUES.FATHER_NAME,
            DEFAULT_VALUES.FATHER_MOBILE,
            DEFAULT_VALUES.MOTHER_NAME,
            DEFAULT_VALUES.MOTHER_MOBILE
          );
        } else {
          defaultDetails.push(
            DEFAULT_VALUES.FATHER_NAME,
            DEFAULT_VALUES.FATHER_MOBILE,
            DEFAULT_VALUES.GUARDIAN_NAME,
            DEFAULT_VALUES.GUARDIAN_MOBILE
        );
        }
      }
    } else if (parentGender === GENDER.MALE) {
      if (maturityStatus === COMMON_CONSTANTS.FATHER) {
        if (isMotherAlive) {
            defaultDetails.push(
                DEFAULT_VALUES.MOTHER_NAME,
                DEFAULT_VALUES.MOTHER_MOBILE,
                DEFAULT_VALUES.GUARDIAN_NAME,
                DEFAULT_VALUES.GUARDIAN_MOBILE
              );
        } else {
            defaultDetails.push(
                DEFAULT_VALUES.GUARDIAN_NAME,
                DEFAULT_VALUES.GUARDIAN_MOBILE
              );
        }
      } else if (maturityStatus === COMMON_CONSTANTS.GUARDIAN) {
        if (isMotherAlive) {
            defaultDetails.push(
                DEFAULT_VALUES.FATHER_NAME,
                DEFAULT_VALUES.FATHER_MOBILE,
                DEFAULT_VALUES.MOTHER_NAME,
                DEFAULT_VALUES.MOTHER_MOBILE
              );
        } else {
            defaultDetails.push(
                DEFAULT_VALUES.FATHER_NAME,
                DEFAULT_VALUES.FATHER_MOBILE,
              );
        }
      }
    }
    return defaultDetails;
  };
  
  export const getDefaultHeader = (parentGender: string, relationshipName: string) => {
    if (parentGender === GENDER.FEMALE) {
      if (relationshipName === COMMON_CONSTANTS.MOTHER) {
        return CHILD_HEADER_LABELS.FATHER_AND_GUARDIAN;
      } else if (relationshipName === COMMON_CONSTANTS.GUARDIAN) {
        return CHILD_HEADER_LABELS.FATHER_AND_MOTHER;
      }
    } else if (parentGender === GENDER.MALE) {
      if (relationshipName === COMMON_CONSTANTS.FATHER) {
        return CHILD_HEADER_LABELS.MOTHER_AND_GUARDIAN;
      } else if (relationshipName === COMMON_CONSTANTS.GUARDIAN) {
        return CHILD_HEADER_LABELS.FATHER_AND_MOTHER;
      }
    }
    return CHILD_OVERVIEW_LABELS.GUARDIAN_DETAILS_HEADER;
  };

/**
 * Parses consent template content into a structured format for rendering consent forms.
 * 
 * @param consentTemplates - Array of consent template objects from the backend.
 * @param benfName - Beneficiary name (if available).
 * @param verifyChildFromGridData - Data used to decode beneficiary name if not directly provided.
 * @returns Structured array for rendering consent content in the UI.
 */
export function parseConsentTemplate(consentTemplates: any, benfName:any, verifyChildFromGridData:any) {
  const result: any = [];

  for (let i = 0; i < consentTemplates.length; i++) {
    const consent = consentTemplates[i];
    let lines = normalizeContent(consent.consent_template_content);
    const obj = {
      consentNumber: toRoman(i + 1),
      mainHeading: lines[0],
      content: [] as any[],
    };

    lines = lines.slice(1);
    let currentItem: any = null;

    lines.forEach((rawLine: string) => {
      const trimmed = rawLine.trim();
      const mainPoint = parseMainPoint(trimmed, benfName, verifyChildFromGridData);
      if (mainPoint) {
        currentItem = mainPoint;
        obj.content.push(currentItem);
        return;
      }
      const alphaSub = parseAlphaSubPoint(trimmed);
      if (alphaSub && currentItem) {
        currentItem.subpoints.push(alphaSub);
        return;
      }
      const romanSub = parseRomanSubPoint(trimmed);
      if (romanSub && currentItem) {
        currentItem.subpoints.push(romanSub);
        return;
      }
      if (currentItem) {
        currentItem.continuation.push(trimmed);
      }
    });
    result.push(obj);
  }
  return result;
}


function normalizeContent(content: string): string[] {
  return content.replace(/\n\s*\n/g, '\n').split(/\n/);
}

function replaceNamePlaceholder(text: string, benficiaryName:any, verifyChildFromGridData:any): string {
  const benfName = getUsername(benficiaryName, verifyChildFromGridData);
  return text?.includes('{#name#}')
    ? text.replace('{#name#}', `${benfName || ''}`)
    : text;
}

function parseMainPoint(line: string, benfName:any, verifyChildFromGridData:any) {
  const mainMatch = line.match(/^(\d+)\.\s+(.*)/);
  if (mainMatch) {
    return {
      key: `${mainMatch[1]}.`,
      value: replaceNamePlaceholder(mainMatch[2], benfName, verifyChildFromGridData),
      subpoints: [],
      continuation: [],
    };
  }
  return null;
}

function parseAlphaSubPoint(line: string) {
  const alphaSubMatch = line.match(/^\(([a-z])\)\s+(.*)/);
  if (alphaSubMatch) {
    return {
      key: `${alphaSubMatch[1]}`,
      value: alphaSubMatch[2],
    };
  }
  return null;
}

function parseRomanSubPoint(line: string) {
  const romanSubMatch = line.match(/^\((i{1,3})\)\s+(.*)/);
  if (romanSubMatch) {
    return {
      key: `${romanSubMatch[1]}`,
      value: romanSubMatch[2],
    };
  }
  return null;
}

function getUsername(benfName:any, verifyChildFromGridData:any) {
  if(benfName) {
    return benfName;
  } else if (verifyChildFromGridData) {
    const decodedUserData = JSON.parse(decodeWithKeyAndShift(verifyChildFromGridData));
    return decodedUserData?.beneficiary?.benf_name;
  } else {
    return '';
  }
}

export const formatNumber = (num: string): string => {
  return `${num?.slice(0, 2)}-${num?.slice(2, 6)}-${num?.slice(6, 10)}-${num?.slice(10)}`;
};

export const sanitizeName = (value: string): string => {
    return value.replace(/[^a-zA-Z\s.]/g, '');
};

export const createErrorMessage = (abdmMessage: string, rchMessage?: string) => (
  <div>
    <p><strong>ABDM:</strong> {abdmMessage}</p>
    {rchMessage && <p><strong>JANANI:</strong> {rchMessage}</p>}
  </div>
);

export const handleError = (message: string, rchMessage?: string) => {
  const errorMessage = createErrorMessage(message, rchMessage);
  toast.error(errorMessage, {
      position: "top-center",
  });
};

/**
 * Maps gender code to gender name
 */
export const mapGenderCodeToName = (genderCode: string): string => {
  if (genderCode === 'F' || genderCode === 'Female') {
    return 'Female';
  }
  return 'Male';
};

/**
 * Finds gender ID from gender response data
 */
export const findGenderIdByName = (genderResponse: any[], genderName: string): number | undefined => {
  return genderResponse?.[0]?.find((item: any) => item?.gender_name === genderName)?.gender_id;
};

/**
 * Formats ABHA number by removing dashes
 */
export const formatAbhaNumber = (abhaNumber: string): string => {
  return abhaNumber ? abhaNumber.replace(/-/g, '') : '';
};

/**
 * Creates date string from year, month, day
 */
export const createDateString = (year: string, month: string, day: string): string => {
  return `${year}-${month}-${day}`;
};

/**
 * Creates payload for updating child KYC details
 */
export const createChildKYCPayload = (
  updatedChildDetails: any,
  currentChild: any,
  childGenderId: number | undefined,
  formattedAbha: string,
  userId: string | null
) => {
  const beneficiaryDob = updatedChildDetails?.yearOfBirth && updatedChildDetails?.monthOfBirth && updatedChildDetails?.dayOfBirth
    ? createDateString(updatedChildDetails.yearOfBirth, updatedChildDetails.monthOfBirth, updatedChildDetails.dayOfBirth)
    : currentChild?.dob;

  return {
    childBenfId: currentChild?.benef_id,
    childName: updatedChildDetails?.name || currentChild?.benf_name,
    abhaId: formattedAbha,
    beneficiaryDob,
    genderId: childGenderId || currentChild?.gender_id,
    weightAtBirth: currentChild?.weight_at_birth_in_gm,
    maturityStatusId: currentChild?.maturity_status_id,
    isMotherAlive: currentChild?.is_mother_alive,
    imageData: null,
    url: null,
    description: null,
    parentUserId: userId || null,
    consentTemplateDetails: null,
    kycVerified: true,
    processChannel: 'web'
  };
};

/**
 * Validates file for upload (type and size)
 */
export const validateFileForUpload = (file: File): { isValid: boolean; error?: string } => {
  const fileType = file.type;
  const fileSize = file.size;
  const maxSize = 1048576; // 1MB
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  if (fileSize > maxSize) {
    return { isValid: false, error: 'File size exceeds 1MB limit' };
  }

  if (!allowedTypes.includes(fileType)) {
    return { isValid: false, error: 'Invalid file type. Only JPEG, PNG, and JPG are allowed' };
  }

  return { isValid: true };
};

/**
 * Creates FormData from file for upload
 */
export const createFileFormData = (file: File): FormData => {
  const formData = new FormData();
  formData.append('file', file);
  return formData;
};

/**
 * Converts base64 image to File object
 */
export const base64ToFile = async (base64Data: string, filename: string = 'aadharImage.png'): Promise<File | null> => {
  const response = await fetch(`data:image/png;base64,${base64Data}`);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
};

/**
 * Refreshes token and gets updated child profile details
 */
export const getRefreshedChildProfile = async (
  refreshToken: string,
  getUpdatedToken: (data: any) => Promise<any>,
  getChildProfileDetails: (data: any) => Promise<any>,
  decodeWithKeyAndShift: (data: string) => string
): Promise<any | null> => {
  const updatedTokenRes = await getUpdatedToken({ token: refreshToken });
  const decodedToken = decodeWithKeyAndShift(updatedTokenRes?.data);
  const updatedToken = JSON.parse(decodedToken);
  
  if (updatedToken?.token) {
    const childProfileResponse = await getChildProfileDetails({ token: updatedToken?.token });
    const decodedChildProfile = decodeWithKeyAndShift(childProfileResponse?.data);
    const childProfileData = JSON.parse(decodedChildProfile);
    return childProfileData;
  }
  return null;
};


/**
 * Handles file change for upload with validation
 */
export const handleFileChangeForUpload = async (
  event: React.ChangeEvent<HTMLInputElement>,
  dispatch: any,
  updateForm: any
): Promise<FormData | undefined> => {
  const selectedFile = event.target.files && event.target.files[0];
  if (!selectedFile) return;

  const validation = validateFileForUpload(selectedFile);
  if (!validation.isValid) {
    return;
  }

  const formData = createFileFormData(selectedFile);
  dispatch(updateForm('file', formData));
  return formData;
};

/**
 * Updates beneficiary profile with photo upload
 */
export const updateBeneficiaryProfile = async (
  benfPhoto: any,
  benfId: any,
  dispatch: any,
  updateForm: any,
  endpoints: any,
  axios: any,
  Auth: any,
  errorLogger: any,
): Promise<FormData | void> => {
  try {
    if (!benfPhoto) return;

    const file = await base64ToFile(benfPhoto);
    if (!file) {
      return;
    }

    const uploadEvent = {
      target: { files: [file] },
    };

    const formData = await handleFileChangeForUpload(
      uploadEvent as unknown as React.ChangeEvent<HTMLInputElement>,
      dispatch,
      updateForm
    );

    if (formData instanceof FormData) {
      formData.append('imageType', 'beneficiary_profile');
      formData.append('objectId', benfId);
      await axios.post(
        `${endpoints.beneficiaryImageUpload}`,
        formData,
        Auth.loggedInWithMultipart()
      );
    }
  } catch (error: any) {
    errorLogger(error?.response?.data || error?.response || error);
  }
};

/**
 * Simplified version for just preparing image data without upload
 */
export const prepareBeneficiaryImageForDisplay = async (
  profilePhoto: any,
): Promise<any | null> => {
    if (!profilePhoto) return null;

    const file = await base64ToFile(profilePhoto);
    if (!file) {
      return null;
    }

    const validation = validateFileForUpload(file);
    if (!validation.isValid) {
      return null;
    }

    const formData = createFileFormData(file);
    const imageUrl = URL.createObjectURL(file);
    
    return {
      imageUrl,
      formData
    };
};

export const handleChildABDMError = (response: any) => {
  if (response?.message?.includes(child_otp_const.gatewayUnavailable)) {
    handleError(response?.message);
  } else if (response?.message?.includes(child_otp_const.runtimeError)) {
    handleError(`${response?.code} - ${response?.message}`, benef_error_const.urlNotFound);
  } else if (response?.data && response?.data?.code === ABDM_ERROR_CODES.ABDM_1157 && response?.data?.message.startsWith(CHILD_ERROR_MESSAGES.ABHA_LIMIT_EXCEEDED)) {
    handleError(`${response?.data?.code} - ${response?.data?.message}`);
  } else if(response?.data && response?.data.code === ABDM_ERROR_CODES.ABDM_1157) {
    handleError(`${response?.code} - ${response?.message}`);
  } else {
    handleError(response?.message);
  }
}